import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/api";
import { captureProof } from "@/lib/proof";
import { sizeLabel } from "@/lib/signs";
import { themeOf } from "@/lib/mall";
import SignCanvas from "@/components/signs/SignCanvas";
import { uploadArtwork, useSignConfig } from "@/components/signs/useSignConfig";
import { useSimpleLine } from "./useSimpleLine";
import { FileCheck, NoteField, PriceBar, QtyPicker, ReadyBy, Section, btnChip, dpiCheck, input, money } from "./Fields";

const WALL = "#eef0f3";
const sizeId = (p) => `${p.width_in}x${p.height_in}`;

/** Preset sizes as square chips, and a W × H pair when the product takes any size. */
function SizeBlock({ product, cfg, presets, fixed }) {
    const custom = !fixed && !presets.some((p) => Number(p.width_in) === Number(cfg.width) && Number(p.height_in) === Number(cfg.height));
    const pick = (p) => {
        if (p.id && (product.preset_sizes || []).some((x) => x.id === p.id)) cfg.selectPreset(p.id);
        else { cfg.setWidth(p.width_in); cfg.setHeight(p.height_in); }
    };
    return (
        <div data-testid="size-block">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {presets.map((p) => {
                    const on = Number(p.width_in) === Number(cfg.width) && Number(p.height_in) === Number(cfg.height);
                    return <button key={p.id || sizeId(p)} type="button" onClick={() => pick(p)} className={btnChip(on)} data-testid={`size-preset-${p.id || sizeId(p)}`}>{p.label || sizeLabel(p.width_in, p.height_in)}</button>;
                })}
            </div>
            {!fixed && (
                <div className="mt-3 flex items-center gap-2" data-testid="size-custom">
                    <input type="number" inputMode="decimal" step="0.25" min={product.min_width_in || 1} max={product.max_width_in} value={cfg.width} aria-label="Width in inches"
                        onChange={(e) => cfg.setWidth(e.target.value)} className={`${input} ${custom ? "border-2 border-accent-brand" : ""}`} placeholder="Width" data-testid="size-width" />
                    <span className="text-[16px]">×</span>
                    <input type="number" inputMode="decimal" step="0.25" min={product.min_height_in || 1} max={product.max_height_in} value={cfg.height} aria-label="Height in inches"
                        onChange={(e) => cfg.setHeight(e.target.value)} className={`${input} ${custom ? "border-2 border-accent-brand" : ""}`} placeholder="Height" data-testid="size-height" />
                    <span className="text-[16px] text-muted-ink">in</span>
                </div>
            )}
        </div>
    );
}

/** Signs, banners, magnets, business cards and flyers: one canvas for every flat product (5.3). */
export default function SignBody({ product, store, cleanupFee, placeholder, ready, sizeMode }) {
    const cfg = useSignConfig(product, { keepSize: true, store: store?.slug || product.store_slug });
    const line = useSimpleLine({ store, cleanupFee });
    const [designing, setDesigning] = useState(false);
    const stageRef = useRef(null);
    const fileRef = useRef(null);
    const theme = useMemo(() => themeOf(store), [store]);
    const cards = product.pricing_model === "per_sheet_bulk";

    // Simple prices with the default variant and, on cards, both sides (5.2, 4.1); the first size is always preselected
    useEffect(() => {
        if (product.default_variant_id) cfg.setVariantId(product.default_variant_id);
        if (cards) cfg.setOption("print_sides_opt", "double-sided");
        const first = (product.preset_sizes || []).find((p) => p.simple !== false);
        const fallback = (product.size_presets || [])[0];
        if (first) cfg.selectPreset(first.id);
        else if (fallback) { cfg.setWidth(fallback.width_in); cfg.setHeight(fallback.height_in); }
        if (product.qty_presets?.length) cfg.setQuantity(product.qty_presets[0]);
    }, [product.slug]); // eslint-disable-line react-hooks/exhaustive-deps

    const variant = (product.variants || []).find((v) => v.id === cfg.variantId);
    const doubleSided = variant?.print_sides === "double" || cfg.options.print_sides_opt === "double-sided";
    const view = doubleSided ? cfg.view : "front";
    const side = cfg.sides[view];
    const front = cfg.sides.front;
    const check = useMemo(() => dpiCheck(front.file, Number(cfg.width) || front.file?.width_in), [front.file, cfg.width]);
    useEffect(() => { if (check && !check.ok) line.setCleanup(true); if (check?.ok) line.setCleanup(false); }, [check?.ok]); // eslint-disable-line react-hooks/exhaustive-deps

    const upload = async (file, which = view) => {
        if (!file) return;
        const url = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
        cfg.previewImage(which, url);
        try { cfg.attachImage(which, await uploadArtwork(file), url); } catch { cfg.clearImage(which); toast.error("Upload failed. Try again."); }
    };

    const presets = (sizeMode === "fixed" ? (product.preset_sizes || []).filter((p) => p.simple !== false) : (product.preset_sizes?.length ? product.preset_sizes : product.size_presets || []));
    const qtyPresets = product.qty_presets?.length ? product.qty_presets : [1, 2, 5, 10];
    const q = cfg.quote;
    const errors = [...(q?.errors || []), !front.file && "Upload your file"].filter(Boolean);
    const total = (q?.line_total || 0) + line.fee;
    const build = async () => {
        const proof = await captureProof(stageRef.current, `${product.slug}-proof`, WALL);
        return {
            product_slug: product.slug, product_name: product.title_override || product.name,
            image: proof ? mediaUrl(proof.url) : mediaUrl(product.photo_override || product.image_url), proof,
            quantity: q.quantity, unit_price: q.unit_price, line_total: q.line_total,
            summary: [sizeLabel(cfg.width, cfg.height), `×${q.quantity}`, variant?.label].filter(Boolean).join(" · "),
            store,
            selections: {
                kind: "sign", store_slug: store?.slug || product.store_slug, ready_in: product.ready_in,
                variant_id: cfg.variantId, preset_id: cfg.presetId, width: Number(cfg.width) || 0, height: Number(cfg.height) || 0,
                quantity: Number(cfg.quantity) || 0, options: cfg.options,
                art_transform: cfg.sides.front.transform, art_transform_back: cfg.sides.back.file ? cfg.sides.back.transform : undefined,
                design_json: { sides: { front: cfg.sides.front.transform, back: cfg.sides.back.transform }, width: cfg.width, height: cfg.height },
            },
            artwork: cfg.artwork,
        };
    };

    return (
        <>
            <Section testid="design-section">
                <div ref={stageRef} className="relative h-[75vw] max-h-[480px] w-full border border-line" style={{ background: WALL }} data-testid="sign-stage">
                    {Number(cfg.width) > 0 && Number(cfg.height) > 0 ? (
                        <SignCanvas product={product} width={cfg.width} height={cfg.height} artUrl={side.url} options={cfg.options}
                            transform={side.transform} onTransform={(t) => cfg.setTransform(view, t)} doubleSided={doubleSided} theme={theme}
                            sideLabel={doubleSided ? view : undefined} safeZoneIn={product.category === "handheld" ? 0.125 : 0} />
                    ) : (
                        <img src={mediaUrl(product.photo_override || product.image_url)} alt="" className="h-full w-full object-cover" />
                    )}
                    {!side.url && (
                        <button type="button" onClick={() => fileRef.current?.click()} className="absolute inset-x-6 bottom-6 flex min-h-[48px] items-center justify-center border border-ink bg-page text-[16px] font-semibold" data-testid="upload-file">
                            {side.busy ? "Uploading…" : `Upload file${doubleSided && view === "back" ? " · Back" : ""}`}
                        </button>
                    )}
                </div>
                <input ref={fileRef} type="file" accept="image/*,.pdf,.ai,.eps,.svg" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="upload-input" />
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[16px]">
                    <button type="button" onClick={() => setDesigning((v) => !v)} className="min-h-[44px] underline" data-testid="design-it">{designing ? "Close designer" : "Design it"}</button>
                    {side.url && <button type="button" onClick={() => fileRef.current?.click()} className="min-h-[44px] underline" data-testid="replace-file">Replace file</button>}
                </div>
                {designing && (
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="inline-designer">
                        {doubleSided && ["front", "back"].map((s) => <button key={s} type="button" onClick={() => cfg.setView(s)} className={btnChip(view === s)} data-testid={`side-tab-${s}`}>{s === "front" ? "Side 1" : "Side 2"}{cfg.sides[s].file ? " •" : ""}</button>)}
                        {doubleSided && view === "back" && front.url && !cfg.sides.back.url && <button type="button" onClick={cfg.copyFrontToBack} className="min-h-[44px] underline" data-testid="use-front-image">Use the front image</button>}
                        {side.url && ["cover", "contain"].map((fit) => <button key={fit} type="button" onClick={() => cfg.setTransform(view, { ...side.transform, fit })} className={btnChip((side.transform.fit || "cover") === fit)} data-testid={`fit-${fit}`}>{fit === "cover" ? "Fill" : "Fit"}</button>)}
                        <p className="w-full text-[14px] text-muted-ink">Drag the image to place it.</p>
                    </div>
                )}
                <FileCheck check={check} cleanup={line.cleanup} setCleanup={line.setCleanup} fee={cleanupFee} />
            </Section>
            <Section title="Size and quantity" testid="size-section">
                <SizeBlock product={product} cfg={cfg} presets={presets} fixed={sizeMode === "fixed" || !!(product.preset_sizes || []).length} />
                <div className="mt-4">
                    <QtyPicker presets={qtyPresets} value={Number(cfg.quantity) || 0} onChange={cfg.setQuantity} />
                </div>
            </Section>
            <NoteField value={line.note} onChange={line.setNote} placeholder={product.note_placeholder || placeholder} />
            <ReadyBy store={store} readyIn={product.ready_in} extraDays={line.extraDays} fallback={ready} needBy={line.needBy} setNeedBy={line.setNeedBy} />
            <PriceBar total={total} busy={line.busy || cfg.pricing} errors={errors}
                note={q?.quantity ? `${sizeLabel(cfg.width, cfg.height)} · ${q.quantity} × ${money(q.unit_price)}${line.fee ? ` + ${money(line.fee)} cleanup` : ""}` : null}
                onPay={() => line.submit(build, "pay")} onAdd={() => line.submit(build, "cart")} />
        </>
    );
}
